/**
 * Network Graph Data JSON export, reimplemented in React.
 *
 * Replaces the legacy `#download-link-graph-json` anchor (LeftMenuLogic.ejs),
 * which pointed the browser straight at `/graph/user/nodes/...` and saved the
 * server payload verbatim. This module fetches the same payload and rewrites
 * each edge's `context_matrix` into a self-describing
 * `context_statements_map` before saving, so consumers (humans, AI tools)
 * don't need out-of-band knowledge to interpret the nesting:
 *
 *   context_matrix:            { "<context>": { "<statementId>": <weight> } }
 *   context_statements_map:  [ { context: "<context>",
 *                                statements: [ { statement_id, weight } ] } ]
 *
 * Export-only: the server payload (shared with LiveUpdate and the standalone
 * graph viewer) is not changed.
 */

import { downloadBlob } from './visibleStatementExports'
import { useBootstrapStore } from '../../stores/useBootstrapStore'

export function transformGraphForExport(graphJson) {
    const edges = (graphJson.edges || []).map(edge => {
        const { context_matrix, ...rest } = edge
        if (!context_matrix) return edge

        const context_statements_map = Object.entries(context_matrix).map(
            ([context, statementWeights]) => ({
                context,
                statements: Object.entries(statementWeights || {}).map(
                    ([statementId, weight]) => ({
                        // JSON object keys are strings; statement ids are
                        // numeric database ids — restore the number.
                        statement_id: /^\d+$/.test(statementId)
                            ? Number(statementId)
                            : statementId,
                        weight,
                    })
                ),
            })
        )

        return { ...rest, context_statements_map }
    })

    return { ...graphJson, edges }
}

/**
 * On public-URL views (`/<user>/<context>`) `jsonPath` points at the
 * unauthenticated `/graph/public/nodes/...` route, which the backend refuses
 * for private graphs (it swallows the 403 into an empty 200 body). When the
 * logged-in viewer IS the owner, rewrite to the authenticated
 * `/graph/user/nodes/...` route, preserving the context and query params.
 *
 * Ownership comes from the bootstrap store (`isPublic === false` ⇔ the
 * logged-in viewer owns the URL graph, see determineIsPublic) — the legacy
 * `userFactory` reports isLoggedIn()=false on public-URL views even for the
 * owner, so it cannot be the signal here.
 */
function resolveGraphJsonPath() {
    const path = userFactory.getJsonPath()
    const viewerIsOwner = useBootstrapStore.getState().isPublic === false
    if (viewerIsOwner) {
        const publicMatch = path.match(/^\/graph\/public\/nodes\/[^/]+\/(.*)$/)
        if (publicMatch) return `/graph/user/nodes/${publicMatch[1]}`
    }
    return path
}

function exportFilename() {
    const addContextNames = userFactory.existsAddContext()
        ? `_with_${userFactory.getAddContext().join('_')}`
        : ''
    return `infranodus_${userFactory.getCurrentUser()}_${userFactory.getCurrentContext()}${addContextNames}.json`
}

/**
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function downloadGraphJson() {
    const jsonPath =
        typeof userFactory !== 'undefined' && resolveGraphJsonPath()

    if (!jsonPath) {
        return {
            ok: false,
            error: 'Graph data URL is not ready. Please reload the page and try again.',
        }
    }

    try {
        const response = await fetch(jsonPath, {
            headers: { Accept: 'application/json' },
        })
        if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`)
        }
        const body = await response.text()
        // The graph route swallows infrasonic access errors into an empty
        // 200 — treat that as "not accessible", not as a valid empty export.
        if (!body) {
            return {
                ok: false,
                error: 'This graph data is not accessible for export.',
            }
        }
        const graphJson = JSON.parse(body)

        downloadBlob(
            JSON.stringify(transformGraphForExport(graphJson)),
            'application/json;charset=utf-8;',
            exportFilename()
        )

        if (window.myEventEmitter?._events?.['onAnalyticsAction']) {
            window.myEventEmitter.emit('onAnalyticsAction', 'download_graph_json')
        }

        return { ok: true }
    } catch (err) {
        console.error('[graphJsonExport] failed:', err)
        return {
            ok: false,
            error: 'Could not download the graph data. Please try again.',
        }
    }
}
